import styled from '@emotion/styled';
import type { ApexOptions } from 'apexcharts';
import { format } from 'date-fns';
import { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { AsinCell, PercentCell } from 'src/components/cells';
import { useFormatCurrency } from 'src/core/hooks/useFormatCurrency';
import type { ITopProductPerformanceTableData } from 'src/queries/useTopProductPerformance';
import {
  breakpoints,
  Flex,
  FrontTooltip,
  lineClampStyles,
  Skeleton,
  themeStyles,
  themeVars,
} from 'src/ui-components';

export const SparklineBlock = ({
  image,
  short_title,
  asin,
  total_sales,
  previous_year_change_pct,
  sparkline_sales,
}: ITopProductPerformanceTableData) => {
  const formatCurrency = useFormatCurrency();

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        sparkline: {
          enabled: true,
        },
      },
      plotOptions: {
        bar: {
          borderRadiusApplication: 'end',
          borderRadius: 4,
          barHeight: 100,
          dataLabels: {
            position: 'center',
          },
        },
      },
      labels: [],
      stroke: {
        curve: 'smooth',
        width: 1.3,
      },
      colors: ['#869705'],
      xaxis: {
        crosshairs: {
          width: 1,
        },
        labels: {
          formatter: function (val) {
            return format(new Date(val), 'MMM yyyy');
          },
          style: {
            fontSize: '10px',
          },
        },
      },
      yaxis: {
        labels: {
          show: false,
          formatter: function (val) {
            return formatCurrency(val);
          },
        },
      },
      tooltip: {
        intersect: false,
        y: {
          title: {
            formatter: function () {
              return '';
            },
          },
        },
        marker: {
          show: false,
        },
      },
    }),
    [formatCurrency],
  );

  return (
    <Block>
      <ProductBlock>
        <Image $src={image} />
        <DescBlock>
          <FrontTooltip title={short_title} placement={'top'}>
            <Text>{short_title}</Text>
          </FrontTooltip>
          <AsinCell asin={asin?.[0]?.asin || ''} asinLink={asin?.[0]?.asinLink || ''} />
        </DescBlock>
      </ProductBlock>
      <PriceBlock>
        <Sales>{formatCurrency(total_sales)}</Sales>
        <Flex gap={'4px'}>
          <PercentCell value={previous_year_change_pct} colored decimal />
          <PercentText>vs. last year</PercentText>
        </Flex>
      </PriceBlock>
      <Chart
        options={{
          ...options,
          labels: sparkline_sales?.map(({ date }) => date),
        }}
        series={[
          {
            data: sparkline_sales?.map(({ value }) => value),
          },
        ]}
        type="bar"
        height={40}
      />
    </Block>
  );
};

export const TopProductsSkeletonBuilder = () => {
  return (
    <Block>
      <ProductBlock>
        <Skeleton h={'48px'} w={'48px'} />
        <DescBlock>
          <Skeleton h={'20px'} />
          <Skeleton h={'24px'} maw={'100px'} />
        </DescBlock>
      </ProductBlock>
      <PriceBlock>
        <Skeleton h={'24px'} mb={'4px'} maw={'200px'} />
        <Skeleton h={'20px'} maw={'250px'} />
      </PriceBlock>
      <Skeleton h={'40px'} />
    </Block>
  );
};

const Block = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;

  &:not(:last-child) {
    padding-right: 16px;
    border-right: 1px solid #e1e3da;

    ${breakpoints.before.mobile} {
      padding-right: 0;
      padding-bottom: 16px;
      border-right: unset;
      border-bottom: 1px solid #e1e3da;
    }
  }
`;

const ProductBlock = styled('div')`
  display: flex;
  align-items: start;
  gap: 8px;
  width: 100%;
`;

const DescBlock = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Image = styled('div')<{ $src: string }>`
  width: 48px;
  height: 48px;
  border-radius: 4px;
  border: 1px solid #e1e3da;
  background: url(${({ $src }) => $src}) lightgray 50% / cover no-repeat;
`;

const Text = styled('div')`
  ${themeStyles.text.telka.body.md};
  ${lineClampStyles(1)};
  color: ${themeVars.colors.coldGrey900};
  font-style: normal;
  flex: 1;
`;

const Link = styled('a')`
  color: ${themeVars.colors.info600};
  ${themeStyles.text.telka.body.md};
`;

const PriceBlock = styled('div')`
  display: flex;
  flex-direction: column;
`;
const Sales = styled('div')`
  ${themeStyles.text.telkaEx.body.xlBold};
  color: ${themeVars.colors.coldGrey900};
`;

const PercentText = styled('div')`
  ${themeStyles.text.telka.body.md};
  color: ${themeVars.colors.coldGrey500};
`;
